import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";

import FormDescriptionField from "components/Environment/components/FormItems/DescriptionField";
import FormFileContentField from "components/Environment/components/FormItems/FileContentField";
import FormFileUploadField from "components/Environment/components/FormItems/FileUploadField";
import FormIsSecretField from "components/Environment/components/FormItems/IsSecretField";
import FormPathField from "components/Environment/components/FormItems/PathField";
import { Expand } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import Drawer from "ds/components/Drawer";
import Card from "ds/components/Card";

import { ContextAttachedMountedFile, ContextFormFields } from "../types";
import ContentDrawer from "./ContentDrawer";
import styles from "./styles.module.css";

type ContextMountedFilesEditFormProps = {
  index: number;
  onSave: (item: ContextAttachedMountedFile) => void;
};

const ContextMountedFilesEditForm = ({ index, onSave }: ContextMountedFilesEditFormProps) => {
  const {
    watch,
    handleSubmit,
    formState: { isValid },
  } = useFormContext<ContextFormFields>();
  const content = watch(`attachedMountedFiles.${index}.content`);
  const [isContentDrawerVisible, setIsContentDrawerVisible] = useState(false);

  const handleCloseDrawer = useCallback(() => setIsContentDrawerVisible(false), []);
  const handleOpenDrawer = useCallback(() => setIsContentDrawerVisible(true), []);

  const onSubmit = useCallback(
    () =>
      handleSubmit((formData: ContextFormFields) =>
        onSave(formData["attachedMountedFiles"][index])
      )(),
    [onSave, index, handleSubmit]
  );

  return (
    <>
      <Card variant="secondary">
        <Box margin="0 0 large">
          <FormPathField name={`attachedMountedFiles.${index}.path`} />
        </Box>

        <FormIsSecretField
          name={`attachedMountedFiles.${index}.isSecret`}
          description="Secret values are only made available to Runs and Tasks and are not accessible in the web GUI and through the API. Marking files as a secret is a recommended option."
        />

        <FormDescriptionField name={`attachedMountedFiles.${index}.description`} />

        <Box margin="large 0 0" grid gridTemplate="1fr" className={styles.fileWrapper} fullWidth>
          {content !== null && (
            <ButtonIcon
              icon={Expand}
              onPress={handleOpenDrawer}
              size="small"
              className={styles.expandIcon}
              variant="secondary"
            >
              Expand editor
            </ButtonIcon>
          )}

          {content === null && (
            <FormFileUploadField name={`attachedMountedFiles.${index}.file`} required />
          )}

          {content !== null && (
            <FormFileContentField name={`attachedMountedFiles.${index}.content`} />
          )}
        </Box>

        <Box justify="end" align="center" fullWidth margin="large 0 0">
          <Button variant="contrast" onPress={onSubmit} disabled={!isValid}>
            Save
          </Button>
        </Box>
      </Card>
      <Drawer position="fixedRight" visible={isContentDrawerVisible} isResizable>
        {isContentDrawerVisible && (
          <ContentDrawer
            onDrawerClose={handleCloseDrawer}
            name={`attachedMountedFiles.${index}.content`}
            onSave={handleSubmit(onSubmit)}
            isEditing
          />
        )}
      </Drawer>
    </>
  );
};

export default ContextMountedFilesEditForm;
